import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Security } from '@mui/icons-material';
import { Button, Card, CardContent, TextField } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetCapabilitiesQuery } from '../store/hooks';
import type { CapabilityResponse } from '@store/generated/admin';

export default function CapabilitiesListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useGetCapabilitiesQuery();

  const capabilities = data?.capabilities || [];

  const filteredCapabilities = useMemo(() => {
    if (!searchInput.trim()) return capabilities;
    const searchLower = searchInput.toLowerCase();
    return capabilities.filter(
      cap =>
        cap.name.toLowerCase().includes(searchLower) ||
        cap.description?.toLowerCase().includes(searchLower)
    );
  }, [capabilities, searchInput]);

  const handleRowClick = useCallback(
    (cap: CapabilityResponse) => {
      navigate(`/admin-v2/capability/${cap.capability_id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        key: 'name' as const,
        label: 'Name',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Security className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{value as string}</span>
          </div>
        ),
      },
      {
        key: 'description' as const,
        label: 'Description',
        render: (value: unknown) => (
          <span className="text-gray-600">{(value as string) || '-'}</span>
        ),
      },
      {
        key: 'created_at' as const,
        label: 'Created',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const date = new Date(value as string);
          return (
            <div className="text-sm">
              <div className="text-gray-900">{formatDateTime(date)}</div>
              <div className="text-gray-500">{formatRelativeTime(date)}</div>
            </div>
          );
        },
      },
    ],
    []
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading capabilities</h2>
            <p className="text-gray-600 mt-2">Unable to fetch capabilities list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Capabilities</h1>
          </div>

          <Card className="mb-6">
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search capabilities by name or description..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                size="small"
              />
            </CardContent>
          </Card>

          {isLoading && !data ? (
            <LoadingState message="Loading capabilities..." />
          ) : (
            <DataTable
              title="Capabilities"
              columns={columns}
              data={filteredCapabilities}
              total={filteredCapabilities.length}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage="No capabilities found"
            />
          )}
        </div>
      </main>
    </div>
  );
}
